import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';

function EmailSettings() {
  const [smtpServer, setSmtpServer] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [configName, setConfigName] = useState('');
  const [useSSL, setUseSSL] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('');
  const [userId, setUserId] = useState('');
  const [limits, setLimits] = useState(null);
  const [existingConfigs, setExistingConfigs] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;


  useEffect(() => {
    // Get user ID from authentication context or localStorage
    const storedUserId = localStorage.getItem('userId') || localStorage.getItem('user_id');
    if (storedUserId) {
      setUserId(storedUserId);
      loadExistingConfigs(storedUserId);
    }
  }, []);

  const loadExistingConfigs = async (uid) => {
    try {
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const response = await fetch(`${API_BASE_URL}/api/smtp/configs?user_id=${uid}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setExistingConfigs(data.smtp_configs);
          // Load the first config if available
          if (data.smtp_configs.length > 0) {
            const config = data.smtp_configs[0];
            setSmtpServer(config.smtp_server);
            setSmtpPort(config.smtp_port.toString());
            setEmail(config.email);
            setConfigName(config.name);
            setUseSSL(config.use_tls);
          }
        }
      }
    } catch (err) {
      console.error('Error loading SMTP configs:', err);
    }
  };

  const validateInputs = () => {
    if (!smtpServer.trim()) return 'SMTP Server is required';
    if (!smtpPort.trim()) return 'SMTP Port is required';
    if (!email.trim()) return 'Email Address is required';
    if (!password.trim()) return 'Password is required';
    if (!userId) return 'User authentication required';
    
    if (!/^\d+$/.test(smtpPort) || parseInt(smtpPort) < 1 || parseInt(smtpPort) > 65535) {
      return 'Port must be a valid number between 1 and 65535';
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      return 'Please enter a valid email address';
    }
    
    return null;
  };

  const handleSaveAndTest = async (e) => {
    e.preventDefault();
    
    setError('');
    setSuccess('');
    setConnectionStatus('Testing connection...');
    setLimits(null);
    
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      setConnectionStatus('');
      return;
    }

    setLoading(true);
    
    try {
const response = await fetch(`${API_BASE_URL}/api/smtp/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          smtp_server: smtpServer,
          smtp_port: parseInt(smtpPort),
          email: email,
          password: password,
          use_tls: useSSL,
          user_id: userId,
          config_name: configName || 'Default SMTP'
        })
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setConnectionStatus('');
        setSuccess('SMTP configuration saved and tested successfully!');
        setLimits(data.limits);
        // Reload configs to show updated list
        loadExistingConfigs(userId);
      } else {
        setError(data.error || 'Configuration test failed');
        setConnectionStatus('');
      }
    } catch (err) {
      setError(`Connection error: ${err.message}`);
      setConnectionStatus('');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = (config) => {
    setSmtpServer(config.smtp_server);
    setSmtpPort(config.smtp_port.toString());
    setEmail(config.email);
    setConfigName(config.name);
    setUseSSL(config.use_tls);
    setPassword(''); // Don't load password for security
  };

  const getCommonSmtpSettings = (provider) => {
    const providers = {
      gmail: { server: 'smtp.gmail.com', port: '587', ssl: true },
      outlook: { server: 'smtp.office365.com', port: '587', ssl: true },
      yahoo: { server: 'smtp.mail.yahoo.com', port: '587', ssl: true }
    };
    
    if (providers[provider]) {
      setSmtpServer(providers[provider].server);
      setSmtpPort(providers[provider].port);
      setUseSSL(providers[provider].ssl);
    }
  };

  if (!userId) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
            <p className="text-gray-600">Please log in to access email settings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Email Settings</h1>
        </div>
        
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded shadow">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded shadow">
            {success}
          </div>
        )}
        
        {connectionStatus && (
          <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 mb-4 rounded shadow flex items-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {connectionStatus}
          </div>
        )}

        {limits && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-blue-800 mb-2">Sending Limits</h3>
            <div className="text-sm text-blue-700">
              <p>Hourly limit: {limits.limit} emails</p>
              <p>Used: {limits.used} emails</p>
              <p>Available: {limits.available} emails</p>
            </div>
          </div>
        )}

        {existingConfigs.length > 0 && (
          <div className="bg-white shadow-md rounded-lg p-6 mb-4">
            <h2 className="text-lg font-semibold mb-4">Existing Configurations</h2>
            <div className="space-y-2">
              {existingConfigs.map((config) => (
                <div key={config._id} className="flex items-center justify-between p-3 border rounded">
                  <div>
                    <span className="font-medium">{config.name}</span>
                    <span className="text-gray-500 ml-2">({config.email})</span>
                  </div>
                  <button
                    onClick={() => loadConfig(config)}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    Load
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <form onSubmit={handleSaveAndTest} className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
          <div className="flex justify-end mb-4 space-x-2">
            <button type="button" onClick={() => getCommonSmtpSettings('gmail')} className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-2 py-1 rounded">
              Gmail
            </button>
            <button type="button" onClick={() => getCommonSmtpSettings('outlook')} className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-2 py-1 rounded">
              Outlook
            </button>
            <button type="button" onClick={() => getCommonSmtpSettings('yahoo')} className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-800 px-2 py-1 rounded">
              Yahoo
            </button>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Configuration Name
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              placeholder="Default SMTP"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              SMTP Server <span className="text-red-500">*</span>
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text"
              value={smtpServer}
              onChange={(e) => setSmtpServer(e.target.value)}
              placeholder="smtp.gmail.com"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              SMTP Port <span className="text-red-500">*</span>
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="text"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              placeholder="587"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
            <p className="text-gray-500 text-xs mt-1">
              For Gmail/Yahoo, use App Password. 
              <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline ml-1">
                Learn more
              </a>
            </p>
          </div>
          
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-blue-600"
                checked={useSSL}
                onChange={(e) => setUseSSL(e.target.checked)}
              />
              <span className="ml-2 text-gray-700">Use SSL/TLS</span>
            </label>
          </div>
          
          <div className="flex items-center justify-between">
            <button
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              type="submit"
              disabled={loading}
            >
              {loading ? 'Testing...' : 'Save & Test Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmailSettings;